class AboutProductCarousel extends HTMLElement {
    connectedCallback() {
        this._container = this.querySelector('[data-carousel-container]');
        if (!this._container) return;

        this._allWrappers = [...this.querySelectorAll('[data-card-wrapper]')];
        this._realCards = this._allWrappers.filter((w) => !w.hasAttribute('data-clone'));
        this._triggerClones = this._allWrappers.filter((w) => w.hasAttribute('data-clone-trigger'));
        this._dots = [...this.querySelectorAll('[data-dot]')];

        if (this._realCards.length === 0) return;

        // Cache MediaQueryList once — avoids creating a new object on every scroll event
        this._desktopMQ = window.matchMedia('(min-width: 768px)');

        this._hoverHandlers = [];
        this._dotHandlers = [];
        this._isJumping = false;
        this._loadHandler = null;
        this._ensureFirstTimer = null;

        this._setRealActive(0);
        this._bindDesktopHover();
        this._initObserver();
        this._initVisibilityObserver();
        this._bindDotClicks();
        this._initCircularLoop();

        // Double rAF: first frame calculates style, second has real offsetLeft values
        requestAnimationFrame(() => requestAnimationFrame(() => this._centerCard(this._realCards[0], 'instant')));

        // Browser scroll restoration fires on/after window load and can override the rAF above.
        // Re-enforce correct position 100ms after load to win that race.
        const ensureFirst = () => {
            this._ensureFirstTimer = null;
            this._centerCard(this._realCards[0], 'instant');
        };
        if (document.readyState === 'complete') {
            this._ensureFirstTimer = setTimeout(ensureFirst, 100);
        } else {
            this._loadHandler = () => {
                this._ensureFirstTimer = setTimeout(ensureFirst, 100);
            };
            window.addEventListener('load', this._loadHandler, { once: true });
        }
    }

    disconnectedCallback() {
        this._realCards?.forEach((_, i) => {
            if (this._hoverHandlers[i]) {
                this._realCards[i].removeEventListener('mouseenter', this._hoverHandlers[i]);
            }
        });
        this._dots?.forEach((dot, i) => {
            if (this._dotHandlers[i]) {
                dot.removeEventListener('click', this._dotHandlers[i]);
            }
        });
        this._observer?.disconnect();
        this._visibilityObserver?.disconnect();
        if (this._loopHandler) {
            this._container.removeEventListener('scroll', this._loopHandler);
        }
        // Clean up the window.load listener and any pending setTimeout
        if (this._loadHandler) {
            window.removeEventListener('load', this._loadHandler);
            this._loadHandler = null;
        }
        if (this._ensureFirstTimer) {
            clearTimeout(this._ensureFirstTimer);
            this._ensureFirstTimer = null;
        }
    }

    _isDesktop() {
        // Reads from cached MediaQueryList instead of creating a new object
        return this._desktopMQ.matches;
    }

    _snapLeft(card) {
        const cRect = this._container.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        // Uses getBoundingClientRect so the result is correct regardless of which element
        // is the card's offsetParent (old Safari skips overflow containers as offsetParent).
        return Math.round(
            this._container.scrollLeft + (cardRect.left - cRect.left) + cardRect.width / 2 - this._container.offsetWidth / 2
        );
    }

    _centerCard(card, behavior = 'smooth') {
        if (!card) return;
        if (this._isDesktop()) {
            this._container.scrollTo({ left: card.offsetLeft, behavior });
        } else {
            this._container.scrollTo({ left: this._snapLeft(card), behavior });
        }
    }

    _setRealActive(realIndex) {
        this._realCards.forEach((card, i) => card.toggleAttribute('data-active', i === realIndex));
        // triggerClones[0] mirrors the last real card (left trigger), triggerClones[1] mirrors the first (right trigger)
        if (this._triggerClones.length >= 2) {
            this._triggerClones[0].toggleAttribute('data-active', realIndex === this._realCards.length - 1);
            this._triggerClones[1].toggleAttribute('data-active', realIndex === 0);
        }
        this._dots.forEach((dot, i) => dot.toggleAttribute('data-active', i === realIndex));
    }

    _bindDesktopHover() {
        this._realCards.forEach((card, index) => {
            const handler = () => {
                if (this._isDesktop()) this._setRealActive(index);
            };
            this._hoverHandlers[index] = handler;
            card.addEventListener('mouseenter', handler);
        });
    }

    _initObserver() {
        if (!('IntersectionObserver' in window)) return;

        this._observer = new IntersectionObserver(
            (entries) => {
                if (this._isDesktop()) return;
                entries.forEach((entry) => {
                    if (entry.intersectionRatio < 0.5 || entry.target.hasAttribute('data-clone')) return;
                    const index = this._realCards.indexOf(entry.target);
                    if (index !== -1) this._setRealActive(index);
                });
            },
            { root: this._container, threshold: 0.5 }
        );

        // Observe only real cards — buffer clones never need to update active state
        this._realCards.forEach((w) => this._observer.observe(w));
    }

    _initVisibilityObserver() {
        if (!('IntersectionObserver' in window)) return;

        this._visibilityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    entry.target.toggleAttribute('data-fully-visible', entry.intersectionRatio >= 1);
                });
            },
            { root: this._container, threshold: 1.0 }
        );

        this._allWrappers.forEach((w) => this._visibilityObserver.observe(w));
    }

    _bindDotClicks() {
        this._dots.forEach((dot, index) => {
            const handler = () => {
                const target = this._realCards[index];
                if (!target) return;
                this._centerCard(target);
                this._setRealActive(index);
            };
            this._dotHandlers[index] = handler;
            dot.addEventListener('click', handler);
        });
    }

    _initCircularLoop() {
        if (this._triggerClones.length < 2) return;

        // triggerClones[0] = clone of last real card (left trigger)
        // triggerClones[1] = clone of first real card (right trigger)
        const triggerLeft = this._triggerClones[0];
        const triggerRight = this._triggerClones[1];

        const cloneCenter = (el) => {
            const r = el.getBoundingClientRect();
            return r.left + r.width / 2;
        };

        // Detect the loop trigger DURING scroll (mid-momentum), not after scrollend.
        // scrollend fires after CSS snap settles on the buffer clone — by then the user has
        // already seen the wrong card. Checking on every scroll event fires the instant jump
        // as soon as the trigger clone crosses center, before the buffer clone is ever reached.
        const tryLoop = () => {
            if (this._isDesktop() || this._isJumping) return;

            const cRect = this._container.getBoundingClientRect();
            const containerCenter = cRect.left + this._container.offsetWidth / 2;

            if (cloneCenter(triggerLeft) >= containerCenter - 10) {
                this._isJumping = true;
                const realLast = this._realCards[this._realCards.length - 1];
                this._container.scrollTo({ left: this._snapLeft(realLast), behavior: 'instant' });
                this._setRealActive(this._realCards.length - 1);
                setTimeout(() => { this._isJumping = false; }, 100);
            } else if (cloneCenter(triggerRight) <= containerCenter + 10) {
                this._isJumping = true;
                this._container.scrollTo({ left: this._snapLeft(this._realCards[0]), behavior: 'instant' });
                this._setRealActive(0);
                setTimeout(() => { this._isJumping = false; }, 100);
            }
        };

        this._loopHandler = tryLoop;
        this._container.addEventListener('scroll', this._loopHandler, { passive: true });
    }

}

if (!customElements.get('about-product-carousel')) {
    customElements.define('about-product-carousel', AboutProductCarousel);
}
